import { ConversionError } from "../Forms/converters";
import { SingleInputValidationParams } from "./validationRunner";
import {
	CombinedInputValidationResult,
	getCombinedState,
	NodeValidationState
} from "./state";
import { IValidationStep } from "./steps";
import { PathRedirect } from "./serverErrors";
import { ModelPath } from "./path";

export enum InputValidationResultKind {
	Valid = 0,
	Invalid = 1
}

export type ValidInputValidationResult = {
	kind: InputValidationResultKind.Valid;
};

export type InvalidInputValidationResult = {
	kind: InputValidationResultKind.Invalid;
	errorMessage: string;
};

export type InputValidationResult = ValidInputValidationResult | InvalidInputValidationResult;

export function isInvalid(result: InputValidationResult): result is InvalidInputValidationResult {
	return result.kind === InputValidationResultKind.Invalid;
}

export type ValidationEventArgs = {
	conversionError?: ConversionError;
	controlValidation?: InputValidationResult;
};

export interface IInputValidation {
	triggerValueChanged(args?: ValidationEventArgs): void;
	triggerInputFinished(args?: ValidationEventArgs): void;
	clearServerErrors: () => void;

	getValidationState(): CombinedInputValidationResult;
	isValid(): boolean;
}

export interface ICollectionValidation<TCollectionItem> extends IInputValidation {
	forIndex(index: number): IValueValidation<TCollectionItem>;
}

export interface IObjectValidation<TModel> extends IInputValidation {
	for<TProperty extends StringKeyOf<TModel>>(propertyName: TProperty): IValueValidation<TModel[TProperty]>;
}

export type IValueValidation<T> =
	[T] extends [PrimitiveType] ? IInputValidation :
	[T] extends [ReadonlyArray<infer R>] ? ICollectionValidation<R> :
	IObjectValidation<T>;

export interface IRootValidation<TModel> extends IObjectValidation<TModel> {
	validate(signal?: AbortSignal): Promise<InputValidationResult>;
	validateStep(validationStep: IValidationStep, signal?: AbortSignal): Promise<InputValidationResult>;
	setServerErrors(serverErrors: ReadonlyMap<ModelPath, InvalidInputValidationResult>): void;
	clearServerErrors(validationStep?: IValidationStep): void;
	tryProcessServerError(error: unknown, pathRedirects?: readonly PathRedirect[]): boolean;
	getValidationStepStates(validationStep: IValidationStep): readonly CombinedInputValidationResult[];
	reset(): void;
}

export type QueueValidationCheckCallback = (
	params: SingleInputValidationParams,
	conversionError: ConversionError | undefined,
	controlValidation: InputValidationResult | undefined
) => void;

export class ValueValidation<TValue, TArrayValue> implements
	IObjectValidation<TValue>,
	ICollectionValidation<TArrayValue> {

	private readonly _modelPath: ModelPath;
	private readonly _queueValidationCheckCallback: QueueValidationCheckCallback;
	private readonly _nodeStates: readonly NodeValidationState[];
	private readonly _onClearServerErrors: (path: ModelPath) => void;

	constructor (
		modelPath: ModelPath,
		queueValidationCheckCallback: QueueValidationCheckCallback,
		nodeStates: readonly NodeValidationState[],
		onClearServerErrors: (path: ModelPath) => void
	) {
		this._modelPath = modelPath;
		this._queueValidationCheckCallback = queueValidationCheckCallback;
		this._nodeStates = nodeStates;
		this._onClearServerErrors = onClearServerErrors;
	}

	public getValidationState(): CombinedInputValidationResult {
		return getCombinedState(this._nodeStates, this._modelPath);
	}

	public isValid(): boolean {
		return this.getValidationState()
			.kind === InputValidationResultKind.Valid;
	}

	public triggerValueChanged(args: ValidationEventArgs = {}): void {
		this._queueValidationCheckCallback(
			{
				initiatorModelPath: this._modelPath,
				action: "input"
			},
			args.conversionError,
			args.controlValidation
		);
	}

	public triggerInputFinished(args: ValidationEventArgs = {}): void {
		this._queueValidationCheckCallback(
			{
				initiatorModelPath: this._modelPath,
				action: "inputFinished"
			},
			args.conversionError,
			args.controlValidation
		);
	}

	public clearServerErrors(): void {
		this._onClearServerErrors(this._modelPath);
	}

	public for<TProperty extends StringKeyOf<TValue>>(
		propertyName: TProperty
	): IValueValidation<TValue[TProperty]> {
		const result: unknown = new ValueValidation(
			this._modelPath.for(propertyName),
			this._queueValidationCheckCallback,
			this._nodeStates,
			this._onClearServerErrors
		);

		return result as IValueValidation<TValue[TProperty]>;
	}

	public forIndex(index: number): IValueValidation<TArrayValue> {
		const result: unknown = new ValueValidation(
			this._modelPath.forIndex(index),
			this._queueValidationCheckCallback,
			this._nodeStates,
			this._onClearServerErrors
		);

		return result as IValueValidation<TArrayValue>;
	}
}